// Centralised SEO config + JSON-LD schema generators.
export const SITE_URL = (
  process.env.REACT_APP_SITE_URL || "https://lakatelier.com"
).replace(/\/$/, "");

export const SITE_NAME = "LAK Atelier";
export const SITE_TAGLINE = "Thoughtfully designed. Strategically built.";
export const SITE_DESCRIPTION =
  "LAK Atelier is a boutique web design studio creating bespoke websites for independent brands. Your website is built free, with a £99 monthly retainer that includes hosting, support and ongoing refinements.";

export const DEFAULT_OG_IMAGE = `${SITE_URL}/favicon-512.png`;

export const NAP = {
  name: SITE_NAME,
  email: "info@lakatelier.com",
  region: "London",
  country: "United Kingdom",
  countryCode: "GB",
  url: SITE_URL,
  sameAs: [],
};

const ld = (obj) => obj;

export const organizationSchema = () =>
  ld({
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: SITE_NAME,
    legalName: "LAK Atelier",
    url: SITE_URL,
    logo: `${SITE_URL}/lak-logo.png`,
    image: DEFAULT_OG_IMAGE,
    email: NAP.email,
    description: SITE_DESCRIPTION,
    slogan: SITE_TAGLINE,
    address: {
      "@type": "PostalAddress",
      addressLocality: NAP.region,
      addressCountry: NAP.countryCode,
    },
    areaServed: { "@type": "Country", name: NAP.country },
    sameAs: NAP.sameAs,
  });

export const localBusinessSchema = () =>
  ld({
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${SITE_URL}/#localbusiness`,
    name: SITE_NAME,
    url: SITE_URL,
    image: DEFAULT_OG_IMAGE,
    logo: `${SITE_URL}/lak-logo.png`,
    email: NAP.email,
    priceRange: "£",
    description: SITE_DESCRIPTION,
    address: {
      "@type": "PostalAddress",
      addressLocality: NAP.region,
      addressCountry: NAP.countryCode,
    },
    areaServed: [
      { "@type": "Country", name: "United Kingdom" },
      { "@type": "AdministrativeArea", name: "London" },
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Boutique web design services",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: { "@type": "Service", name: "Custom Website Design" },
          priceCurrency: "GBP",
          price: "0",
          description: "Bespoke website, designed and built free of charge.",
        },
        {
          "@type": "Offer",
          itemOffered: { "@type": "Service", name: "Monthly Retainer" },
          priceCurrency: "GBP",
          price: "99",
          description: "Hosting, security, ongoing SEO, support and refinements.",
        },
      ],
    },
  });

export const websiteSchema = () =>
  ld({
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    url: SITE_URL,
    name: SITE_NAME,
    description: SITE_DESCRIPTION,
    publisher: { "@id": `${SITE_URL}/#organization` },
    inLanguage: "en-GB",
  });

export const webPageSchema = ({ url, title, description, breadcrumbs }) =>
  ld({
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    url,
    name: title,
    description,
    isPartOf: { "@id": `${SITE_URL}/#website` },
    about: { "@id": `${SITE_URL}/#organization` },
    inLanguage: "en-GB",
    breadcrumb: breadcrumbs ? breadcrumbListSchema(breadcrumbs) : undefined,
  });

export const breadcrumbListSchema = (crumbs) =>
  ld({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: c.url,
    })),
  });

export const faqSchema = (items) =>
  ld({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  });

export const serviceSchema = (services) =>
  ld({
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Boutique Web Design",
    provider: { "@id": `${SITE_URL}/#organization` },
    areaServed: { "@type": "Country", name: "United Kingdom" },
    serviceType: "Website design, development, hosting and ongoing support",
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Capabilities",
      itemListElement: services.map((s) => ({
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: s.name, description: s.description },
      })),
    },
  });

export const articleSchema = ({ title, description, url, image, datePublished }) =>
  ld({
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    image,
    url,
    datePublished,
    author: { "@id": `${SITE_URL}/#organization` },
    publisher: { "@id": `${SITE_URL}/#organization` },
  });

export const offerSchema = () =>
  ld({
    "@context": "https://schema.org",
    "@type": "Offer",
    name: "LAK Launch",
    priceCurrency: "GBP",
    price: "0",
    priceSpecification: {
      "@type": "UnitPriceSpecification",
      price: "99",
      priceCurrency: "GBP",
      unitText: "MONTH",
      name: "Hosting, support and SEO retainer",
    },
    description:
      "Bespoke web design and build, included free. Hosting, support and ongoing SEO from £99 a month.",
    seller: { "@id": `${SITE_URL}/#organization` },
  });
